import { useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
} from 'recharts';
import { getProductPrice } from '../../utils/pricing';

export default function MonthlyEvolutionChart() {
    const { products, sales, expenses } = useAppContext();

    const data = useMemo(() => {
        const map = new Map();

        // Processar vendas
        sales.forEach((s) => {
            const d = new Date(s.date);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const monthName = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });

            const product = products.find((p) => p.id === s.productId);
            if (!product) return;

            const price = getProductPrice(product);
            const revenue = price * s.quantity;

            const item = map.get(key) || { key, month: monthName, revenue: 0, expenses: 0, profit: 0 };
            item.revenue += revenue;
            map.set(key, item);
        });

        // Processar despesas
        expenses.forEach((e) => {
            const d = new Date(e.date);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const monthName = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });

            const item = map.get(key) || { key, month: monthName, revenue: 0, expenses: 0, profit: 0 };
            item.expenses += e.amount;
            map.set(key, item);
        });

        // Calcular lucro
        const result = Array.from(map.values()).map(item => ({
            ...item,
            profit: item.revenue - item.expenses
        }));

        return result.sort((a, b) => a.key.localeCompare(b.key));
    }, [products, sales, expenses]);

    if (!data.length) {
        return (
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h2 className="h6 mb-2">Evolução Mensal</h2>
                    <p className="text-muted small mb-0">
                        Lance vendas e despesas para visualizar a evolução mensal.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body">
                <h2 className="h6 mb-3">Evolução Mensal - Receita, Despesas e Lucro</h2>
                <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                        <ComposedChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                            />
                            <Legend />
                            <Bar
                                dataKey="revenue"
                                fill="#0d6efd"
                                name="Receita"
                            />
                            <Bar
                                dataKey="expenses"
                                fill="#dc3545"
                                name="Despesas"
                            />
                            <Line
                                type="monotone"
                                dataKey="profit"
                                stroke="#198754"
                                strokeWidth={3}
                                name="Lucro"
                                dot={{ fill: '#198754', r: 5 }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
